import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { CircleNotch } from "@phosphor-icons/react";
import { Logo } from "@/components/Logo";

const BG_IMG =
    "https://images.unsplash.com/photo-1774463781617-9dc583b82fbe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwzfHxjYXR0bGUlMjBmYXJtJTIwc3VucmlzZXxlbnwwfHx8fDE3NzY0OTE0NzN8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
    const { login } = useAuth();
    const { t, lang, setLang } = useT();
    const nav = useNavigate();
    const [email, setEmail] = useState("admin@fcr.app");
    const [password, setPassword] = useState("admin123");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const r = await login(email, password);
        setLoading(false);
        if (r.ok) {
            toast.success(t("welcome"));
            nav("/", { replace: true });
        } else {
            toast.error(r.error);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2">
            {/* Left: image */}
            <div
                className="hidden lg:block relative"
                style={{
                    backgroundImage: `url(${BG_IMG})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
            >
                <div className="absolute inset-0 bg-black/40" />
                <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
                    <Logo
                        size={38}
                        primary="#FFFFFF"
                        accent="#D99533"
                        wordmarkClass="text-white text-xl"
                        subtitle={t("tagline")}
                        subtitleClass="text-white/70"
                    />
                    <div className="max-w-md">
                        <h1 className="font-display text-4xl lg:text-5xl font-semibold tracking-tight mb-4">
                            {t("login_hero_title")}
                        </h1>
                        <p className="text-white/85 text-base leading-relaxed">
                            {t("login_hero_desc")}
                        </p>
                    </div>
                </div>
            </div>

            {/* Right: form */}
            <div className="flex items-center justify-center p-6 sm:p-10 bg-background">
                <Card className="w-full max-w-md p-8 card-paper shadow-none" data-testid="login-card">
                    <div className="flex items-start justify-between mb-6">
                        <div className="lg:hidden">
                            <Logo size={30} primary="#1A3626" accent="#D99533" />
                        </div>
                        <div className="ml-auto flex gap-1 rounded-md bg-[#E4EAE1] p-1" data-testid="lang-switcher-login">
                            {["es", "en"].map((l) => (
                                <button
                                    key={l}
                                    onClick={() => setLang(l)}
                                    className={`text-xs font-medium px-2.5 py-1 rounded transition-colors ${lang === l ? "bg-white text-[#1A3626]" : "text-[#1A3626]/60"}`}
                                    data-testid={`login-lang-${l}`}
                                >
                                    {l === "es" ? "ES" : "EN"}
                                </button>
                            ))}
                        </div>
                    </div>
                    <h2 className="font-display text-3xl font-semibold tracking-tight mb-1">{t("login_title")}</h2>
                    <p className="text-sm text-muted-foreground mb-6">
                        {t("login_subtitle")}
                    </p>
                    <form onSubmit={submit} className="space-y-4">
                        <div className="space-y-1.5">
                            <Label htmlFor="email">{t("email")}</Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="admin@fcr.app"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                data-testid="login-email-input"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="password">{t("password")}</Label>
                            <Input
                                id="password"
                                type="password"
                                placeholder="••••••••"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                data-testid="login-password-input"
                            />
                        </div>
                        <Button
                            type="submit"
                            disabled={loading}
                            className="w-full h-11 bg-[#1A3626] hover:bg-[#132A1D] text-white"
                            data-testid="login-submit-btn"
                        >
                            {loading ? <CircleNotch className="animate-spin mr-2" size={16} /> : null}
                            {t("enter")}
                        </Button>
                    </form>
                    <div className="mt-6 text-sm text-muted-foreground text-center">
                        {t("no_account")}{" "}
                        <Link to="/registro" className="text-[#1A3626] font-medium hover:underline" data-testid="go-register">
                            {t("create_account")}
                        </Link>
                    </div>
                    <div className="mt-6 p-3 rounded-md bg-[#E4EAE1]/60 text-xs text-[#1A3626]">
                        <b>{t("demo")}:</b> admin@fcr.app / admin123
                    </div>
                </Card>
            </div>
        </div>
    );
}
